import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Phone as PhoneIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ProvisionPhoneNumberModal } from '@/components/settings/ProvisionPhoneNumberModal'
import { useAppAuth } from '@/contexts/app-auth.context'
import {
  TimezoneSelector,
  type TimezoneEnumValue,
} from '@/components/common/TimezoneSelector'
import { WeeklyHoursSchedule } from '@/components/common/WeeklyHoursSchedule'
import {
  DEFAULT_WEEKLY_SCHEDULE,
  DEFAULT_TIMEZONE,
  toTimezoneEnumValue,
} from '@super/shared-types'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormChangeTracking,
} from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'

const formSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  timezoneName: z.string().optional(),
  officeHours: z.record(z.string(), z.any()).optional(), // WeeklySchedule is a record of day schedules
})

type FormValues = z.infer<typeof formSchema>

interface CompanyDetailsFormProps {
  currentCompany: any
  isCompanyLoading: boolean
  onSubmit: (values: FormValues) => Promise<boolean>
  saving: boolean
}

function normalizeTimezoneForForm(
  timezoneName?: string | null,
): TimezoneEnumValue {
  return toTimezoneEnumValue(timezoneName ?? undefined) ?? DEFAULT_TIMEZONE
}

const MANUAL_PHONE_PROVISIONING_FLAG = 'manual_phone_provisioning'

function formatE164(number: string): string {
  const match = number.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return match ? `+1 (${match[1]}) ${match[2]}-${match[3]}` : number
}

export function CompanyDetailsForm({
  currentCompany,
  isCompanyLoading,
  onSubmit,
  saving,
}: CompanyDetailsFormProps) {
  const { featureFlags, refetchCurrentCompany } = useAppAuth()
  const phoneProvisioningEnabled = featureFlags.includes(
    MANUAL_PHONE_PROVISIONING_FLAG,
  )
  const [phoneModalOpen, setPhoneModalOpen] = useState(false)
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: currentCompany?.name ?? '',
      timezoneName: normalizeTimezoneForForm(currentCompany?.timezoneName),
      officeHours:
        currentCompany?.officeHours?.weeklySchedule ?? DEFAULT_WEEKLY_SCHEDULE,
    },
  })

  useEffect(() => {
    if (currentCompany && !isCompanyLoading) {
      const formData = {
        name: currentCompany.name ?? '',
        timezoneName: normalizeTimezoneForForm(currentCompany.timezoneName),
        officeHours:
          currentCompany.officeHours?.weeklySchedule ?? DEFAULT_WEEKLY_SCHEDULE,
      }

      form.reset(formData)
    }
  }, [currentCompany?.id, isCompanyLoading, form])

  // Component to handle floating island that must be inside Form provider
  const FloatingIslandContent = () => {
    const { hasUnsavedChanges, changedFieldsCount } = useFormChangeTracking()

    return (
      <FloatingActionIslandContent
        show={hasUnsavedChanges}
        componentId="company-settings-save"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span className="font-medium">
              <span className="mr-1">
                {changedFieldsCount} <span>unsaved&nbsp;</span>
                {changedFieldsCount === 1 ? 'change' : 'changes'}
              </span>
            </span>
          </div>

          <div className="flex items-center space-x-2 justify-end pointer-events-auto">
            <Button
              onClick={form.handleSubmit(handleSubmit)}
              disabled={saving}
              size="sm"
              variant="form"
            >
              <span>{saving ? 'Saving...' : 'Save changes'}</span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
    )
  }

  const handleSubmit = async (values: FormValues) => {
    const normalizedValues = {
      ...values,
      timezoneName: normalizeTimezoneForForm(values.timezoneName),
    }

    const success = await onSubmit(normalizedValues)
    if (success) {
      // Reset form to mark as not dirty (hide floating island)
      form.reset(normalizedValues)
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Company Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="max-w-md">
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="timezoneName"
                render={({ field }) => (
                  <FormItem className="max-w-md">
                    <FormLabel>Timezone</FormLabel>
                    <FormControl>
                      <TimezoneSelector
                        value={field.value as TimezoneEnumValue}
                        onValueChange={field.onChange}
                        placeholder="Select your company's timezone"
                        disabled={saving}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="officeHours"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Business Hours</FormLabel>
                    <FormControl>
                      <WeeklyHoursSchedule
                        value={field.value || DEFAULT_WEEKLY_SCHEDULE}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
      </form>

      {phoneProvisioningEnabled && currentCompany && (
        <Card>
          <CardHeader>
            <CardTitle>Phone Number</CardTitle>
          </CardHeader>
          <CardContent>
            {currentCompany.phone ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {formatE164(currentCompany.phone)}
                  </span>
                  <Badge variant="secondary">Voice</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Voice only — SMS coming soon.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Provision a Twilio number to receive inbound voice calls. SMS
                  is not enabled yet.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPhoneModalOpen(true)}
                >
                  Provision a phone number
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {phoneProvisioningEnabled && currentCompany && (
        <ProvisionPhoneNumberModal
          open={phoneModalOpen}
          companyId={currentCompany.id}
          companyName={currentCompany.name}
          onOpenChange={setPhoneModalOpen}
          onProvisioned={() => {
            refetchCurrentCompany()
          }}
        />
      )}

      <FloatingIslandContent />
    </Form>
  )
}
